import React, { useCallback } from 'react'

import { type VariantProps, cva } from 'class-variance-authority'
import { LuCheckCircle } from 'react-icons/lu'
import { twMerge } from 'tailwind-merge'

import { Badge } from '@/components/common/BadgeV2'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/common/TooltipV3'

import type { StatusType } from '@/types/Status'

export const badgeVariants = cva(
  'inline-flex items-center gap-1.5 text-[13px] py-0 px-1.5',
  {
    variants: {
      variant: {
        amber:
          'text-amber-700 dark:text-amber-300 border-amber-400 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/60',
        orange:
          'text-orange-700 dark:text-orange-200 border-orange-400 dark:border-orange-600 bg-orange-50 dark:bg-orange-900/60',
        default:
          'text-primary-700 dark:text-primary-300 border-primary-400 dark:border-primary-500 bg-primary-50 dark:bg-primary-900',
        blue: 'text-blue-700 dark:text-blue-200 border-blue-400 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/60',
        green:
          'text-green-700 dark:text-green-200 border-green-400 dark:border-green-600 bg-green-50 dark:bg-green-900/60',
        red: 'text-red-700 dark:text-red-200 border-red-400 dark:border-red-600 bg-red-100 dark:bg-red-900/70',
        purple:
          'text-purple-700 dark:text-purple-200 border-purple-400 dark:border-purple-600 bg-purple-50 dark:bg-purple-900/60',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface PendingBadgeProps extends React.ComponentProps<typeof Badge> {
  className?: string
  isConfirming?: boolean
  inTable?: boolean
  label?: React.ReactNode
  onClick?: () => void
}

export const PendingBadge: React.FC<PendingBadgeProps> = ({
  className,
  isConfirming,
  inTable = false,
  label = 'Confirm',
  onClick,
  ...props
}) => {
  const handleConfirm = useCallback(async () => {
    if (!onClick || isConfirming) return
    onClick()
  }, [isConfirming, onClick])

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <button className="flex items-center" onClick={handleConfirm}>
            <Badge
              variant="outline"
              className={twMerge(
                badgeVariants({ variant: 'amber' }),
                'whitespace-nowrap',
                onClick && 'cursor-pointer transition-colors duration-100',
                className,
              )}
              {...props}
            >
              {isConfirming ? (
                <>
                  <Spinner className="w-3 h-3" variant="amber-compact" />
                  <span>Confirming...</span>
                </>
              ) : (
                <>
                  <LuCheckCircle className="!w-[12px] !h-[12px] flex-shrink-0" />
                  <span>{label}</span>
                </>
              )}
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent className="space-y-2 max-w-[300px] py-2" side="right">
          <p>
            This item is pending approval to add to your library. It will not be
            used in any Sweetspot workflows until it is confirmed.
          </p>
          <p>
            Click to confirm or delete
            {inTable
              ? ', or select this item with the checkbox to bulk confirm.'
              : '.'}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

interface NotExtractedBadgeProps extends React.ComponentProps<typeof Badge> {
  className?: string
  onClick?: () => void
}

export const NotExtractedBadge: React.FC<NotExtractedBadgeProps> = ({
  className,
  onClick,
  ...props
}) => {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild={true}>
          <button className="flex items-center" onClick={onClick}>
            <Badge
              variant="outline"
              className={twMerge(
                badgeVariants({ variant: 'amber' }),
                onClick && 'cursor-pointer transition-colors duration-100',
                className,
              )}
              {...props}
            >
              Not scanned
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent className="space-y-2 max-w-[300px]" side="right">
          <p>
            Scan this file to identify people, companies, and other key data,
            making them searchable and ready to use in Sweetspot.
          </p>
          {onClick && <p className="font-medium">Click to start scan.</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// region: misc. file status badge

export const indexStatusToLabel: Record<StatusType, string> = {
  not_found: 'Not Indexed',
  pending: 'Pending',
  running: 'Indexing',
  succeeded: 'Indexed',
  failed: 'Index Failed',
}

export const extractionStatusToLabel: Record<StatusType, string> = {
  not_found: 'Not Scanned',
  pending: 'Pending',
  running: 'Scanning',
  succeeded: 'Scanned',
  failed: 'Scan Failed',
}

interface FileStatusBadgeProps extends React.ComponentProps<typeof Badge> {
  className?: string
  status: StatusType
  label: string
  tooltipContentProps?: React.ComponentProps<typeof TooltipContent>
}

const statusToVariant: Record<
  StatusType,
  VariantProps<typeof badgeVariants>['variant']
> = {
  not_found: 'default',
  pending: 'amber',
  running: 'blue',
  succeeded: 'green',
  failed: 'red',
}

const statusToSpinnerVariant: Record<
  StatusType,
  VariantProps<typeof Spinner>['variant']
> = {
  not_found: 'outline-compact',
  succeeded: 'positive-compact',
  failed: 'danger-compact',
  running: 'blue-compact',
  pending: 'amber-compact',
}

export const FileStatusBadge: React.FC<FileStatusBadgeProps> = ({
  className,
  status,
  label,
  children,
  tooltipContentProps,
  ...props
}) => {
  const isRunning = status === 'running' || status === 'pending'

  const {
    className: tooltipContentClassName,
    side = 'top',
    ...rest
  } = tooltipContentProps ?? {}

  const noChildren =
    !children || (Array.isArray(children) && children.every((child) => !child))

  return (
    <Tooltip delayDuration={250}>
      <TooltipTrigger
        className={twMerge(
          'inline-flex items-center',
          children ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        <Badge
          variant="outline"
          className={twMerge(
            badgeVariants({ variant: statusToVariant[status] }),
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-1.5">
            {isRunning && (
              <Spinner
                className="w-3 h-3"
                variant={statusToSpinnerVariant[status]}
              />
            )}
            {label}
          </div>
        </Badge>
      </TooltipTrigger>
      {!noChildren && (
        <TooltipContent
          className={twMerge(
            'space-y-2 max-w-[300px]',
            tooltipContentClassName,
          )}
          side={side}
          {...rest}
        >
          {children}
        </TooltipContent>
      )}
    </Tooltip>
  )
}

// endregion: misc. file status badge

export const YouBadge: React.FC<React.ComponentProps<typeof Badge>> = ({
  className,
  ...props
}) => {
  return (
    <Badge
      variant="blue"
      className="inline-flex items-center whitespace-nowrap text-xs h-[22px]"
      {...props}
    >
      You
    </Badge>
  )
}

export const OurCompanyBadge: React.FC<React.ComponentProps<typeof Badge>> = ({
  className,
  ...props
}) => {
  return (
    <Badge
      variant="blue"
      className="inline-flex items-center whitespace-nowrap text-[13px] h-[22px]"
      {...props}
    >
      Our Company
    </Badge>
  )
}
